export type PlanCode = "starter" | "professional" | "enterprise";
export type BillingInterval = "monthly" | "annual";

export interface PlanEntitlements {
  activeMatterLimit: number | null;
  seatLimit: number | null;
  storageLimitBytes: number | null;
  electronicSignatures: boolean;
  billingAndInvoicing: boolean;
  realtimeNotifications: boolean;
  auditSearch: boolean;
  auditExport: boolean;
  apiAccess: boolean;
  whiteLabel: boolean;
}

export interface PublicPlanFeature {
  text: string;
  included: boolean;
}

export interface PlanDefinition {
  code: PlanCode;
  name: string;
  monthlyPriceKobo: number;
  annualPriceKobo: number;
  monthlyPriceLabel: string;
  annualPriceLabel: string;
  annualSavingsLabel: string;
  description: string;
  ctaLabel: string;
  popular: boolean;
  supportLabel: string;
  features: PublicPlanFeature[];
  entitlements: PlanEntitlements;
}

export interface DerivedSubscription {
  planCode: PlanCode;
  planName: string;
  billingInterval: BillingInterval;
  status: "trial" | "active" | "pending" | "restricted";
  source: "subscription" | "restricted";
  currentPeriodEnd: string | null;
  startedAt: string | null;
  isTrial: boolean;
  entitlements: PlanEntitlements;
}

const MB = 1024 * 1024;

export const TRIAL_DAYS = 14;
export const TRIAL_ELIGIBLE_PLANS: PlanCode[] = ["starter", "professional"];

export const restrictedEntitlements: PlanEntitlements = {
  activeMatterLimit: 0,
  seatLimit: 0,
  storageLimitBytes: 0,
  electronicSignatures: false,
  billingAndInvoicing: false,
  realtimeNotifications: false,
  auditSearch: false,
  auditExport: false,
  apiAccess: false,
  whiteLabel: false,
};

export const planDefinitions: Record<PlanCode, PlanDefinition> = {
  starter: {
    code: "starter",
    name: "Starter",
    monthlyPriceKobo: 7_500_000,
    annualPriceKobo: 75_000_000,
    monthlyPriceLabel: "₦75,000",
    annualPriceLabel: "₦750,000",
    annualSavingsLabel: "Save ₦150,000",
    description: "For solo practitioners and small firms",
    ctaLabel: "Start Starter",
    popular: false,
    supportLabel: "Email support (48hr response)",
    features: [
      { text: "Up to 10 active matters", included: true },
      { text: "2 lawyer seats", included: true },
      { text: "8-stage transaction pipeline", included: true },
      { text: "Document upload & storage (5GB)", included: true },
      { text: "Client portal with visual timeline", included: true },
      { text: "Basic audit log", included: true },
      { text: "Email support (48hr response)", included: true },
      { text: "E-signature (Professional+)", included: false },
      { text: "Billing & invoicing (Professional+)", included: false },
      { text: "API access (Enterprise)", included: false },
    ],
    entitlements: {
      activeMatterLimit: 10,
      seatLimit: 2,
      storageLimitBytes: 5 * 1024 * MB,
      electronicSignatures: false,
      billingAndInvoicing: false,
      realtimeNotifications: false,
      auditSearch: false,
      auditExport: false,
      apiAccess: false,
      whiteLabel: false,
    },
  },
  professional: {
    code: "professional",
    name: "Professional",
    monthlyPriceKobo: 20_000_000,
    annualPriceKobo: 200_000_000,
    monthlyPriceLabel: "₦200,000",
    annualPriceLabel: "₦2,000,000",
    annualSavingsLabel: "Save ₦400,000",
    description: "For growing firms managing multiple transactions",
    ctaLabel: "Choose Professional",
    popular: true,
    supportLabel: "Priority support (24hr response)",
    features: [
      { text: "Everything in Starter, plus:", included: true },
      { text: "Up to 50 active matters", included: true },
      { text: "10 seats", included: true },
      { text: "E-signature with SHA-256 integrity", included: true },
      { text: "Billing & invoicing with Naira formatting", included: true },
      { text: "Full audit log with search & export", included: true },
      { text: "Real-time notifications", included: true },
      { text: "Document storage (25GB)", included: true },
      { text: "Priority support (24hr response)", included: true },
      { text: "API access (Enterprise)", included: false },
      { text: "White-label (Enterprise)", included: false },
    ],
    entitlements: {
      activeMatterLimit: 50,
      seatLimit: 10,
      storageLimitBytes: 25 * 1024 * MB,
      electronicSignatures: true,
      billingAndInvoicing: true,
      realtimeNotifications: true,
      auditSearch: true,
      auditExport: true,
      apiAccess: false,
      whiteLabel: false,
    },
  },
  enterprise: {
    code: "enterprise",
    name: "Enterprise",
    monthlyPriceKobo: 50_000_000,
    annualPriceKobo: 500_000_000,
    monthlyPriceLabel: "₦500,000",
    annualPriceLabel: "₦5,000,000",
    annualSavingsLabel: "Save ₦1,000,000",
    description: "For large firms and organizations",
    ctaLabel: "Choose Enterprise",
    popular: false,
    supportLabel: "Phone support",
    features: [
      { text: "Everything in Professional, plus:", included: true },
      { text: "Unlimited matters & seats", included: true },
      { text: "API access for system integration", included: true },
      { text: "White-label branding option", included: true },
      { text: "Dedicated account manager", included: true },
      { text: "Custom onboarding & training", included: true },
      { text: "Unlimited document storage", included: true },
      { text: "SLA with 99.9% uptime guarantee", included: true },
      { text: "Phone support", included: true },
    ],
    entitlements: {
      activeMatterLimit: null,
      seatLimit: null,
      storageLimitBytes: null,
      electronicSignatures: true,
      billingAndInvoicing: true,
      realtimeNotifications: true,
      auditSearch: true,
      auditExport: true,
      apiAccess: true,
      whiteLabel: true,
    },
  },
};

export const publicPlans = [
  planDefinitions.starter,
  planDefinitions.professional,
  planDefinitions.enterprise,
];

export function getPlanDefinition(planCode: PlanCode) {
  return planDefinitions[planCode];
}

export function getPlanIntervalPriceLabel(planCode: PlanCode, billingInterval: BillingInterval) {
  const plan = getPlanDefinition(planCode);
  return billingInterval === "annual" ? plan.annualPriceLabel : plan.monthlyPriceLabel;
}

export function formatStorageLimit(storageLimitBytes: number | null) {
  if (storageLimitBytes === null) {
    return "Unlimited storage";
  }

  const gb = storageLimitBytes / 1024 / 1024 / 1024;
  if (gb >= 1) {
    return `${gb.toFixed(0)}GB storage`;
  }

  return `${Math.round(storageLimitBytes / MB)}MB storage`;
}

export function resolveDerivedSubscription(options: {
  activePlanCode?: PlanCode | null;
  activeBillingInterval?: BillingInterval | null;
  activeStatus?: "trial" | "active" | "pending" | null;
  startedAt?: string | null;
  currentPeriodEnd?: string | null;
  fallbackPlanCode?: PlanCode | null;
}): DerivedSubscription {
  const {
    activePlanCode,
    activeBillingInterval,
    activeStatus,
    startedAt,
    currentPeriodEnd,
    fallbackPlanCode,
  } = options;

  if (activePlanCode && activeStatus) {
    const plan = getPlanDefinition(activePlanCode);
    return {
      planCode: activePlanCode,
      planName: plan.name,
      billingInterval: activeBillingInterval || "monthly",
      status: activeStatus,
      source: activeStatus === "pending" ? "restricted" : "subscription",
      currentPeriodEnd: currentPeriodEnd || null,
      startedAt: startedAt || null,
      isTrial: activeStatus === "trial",
      entitlements: activeStatus === "pending" ? restrictedEntitlements : plan.entitlements,
    };
  }

  const fallback = getPlanDefinition(fallbackPlanCode || "starter");
  return {
    planCode: fallback.code,
    planName: fallback.name,
    billingInterval: activeBillingInterval || "monthly",
    status: "restricted",
    source: "restricted",
    currentPeriodEnd: currentPeriodEnd || null,
    startedAt: startedAt || null,
    isTrial: false,
    entitlements: restrictedEntitlements,
  };
}

export function planSupportsTrial(planCode: PlanCode) {
  return TRIAL_ELIGIBLE_PLANS.includes(planCode);
}

export function getUpgradeTargetForFeature(
  feature:
    | "electronicSignatures"
    | "billingAndInvoicing"
    | "realtimeNotifications"
    | "auditSearch"
    | "auditExport"
    | "apiAccess"
    | "whiteLabel",
): PlanCode {
  if (feature === "apiAccess" || feature === "whiteLabel") {
    return "enterprise";
  }

  return "professional";
}
